import React, {useCallback, useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {Box, useBreakpointValue} from '@chakra-ui/react'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    get_layer_page_category,
    get_layer_page_subcategory,
    retrieve_status_code_gtm,
    virtual_page_view,
} from '../../custom-api/layers/default'
import {hashSHA256} from '../../utils/utils'
import md5 from 'md5'
import {useLocaleCountry, useLocaleLanguage} from '../../hooks/use-locale-custom'
import useUtils from '../../custom-hooks/use-utils-helper'

const GtmPageView = () => {
    const location = useLocation()
    const isRealPageviewPushed = useRef(false)
    const locationChangeEvent = useRef(null)

    const {data: customer, isLoading} = useCurrentCustomer()
    const {data: basket, isLoading: basketIsLoading} = useCurrentBasket()

    const country = useLocaleCountry()
    const language = useLocaleLanguage()

    const {isProductDetail, isCategory} = useUtils().currentPage(location.pathname)
    const sendEvent = !isProductDetail && !isCategory

    const deviceType = useBreakpointValue({base: null, sm: 'm', md: 't', lg: 'd'})
    const {addLayer, addLayerAtPositionZero} = useGoogleTagManager()

    const getStatusCode = retrieve_status_code_gtm()
    const {customParams} = getConfig()

    const hasCustomerData = useCallback(
        async (customer, basket) => {
            const customerFirstName =
                customer?.firstName || localStorage.getItem('guestCustomerFirstName')
            const customerLastName =
                customer?.lastName || localStorage.getItem('guestCustomerLastName')
            const email =
                customer?.email ||
                basket?.customerInfo?.email ||
                localStorage.getItem('guestCustomerEmail')
            const id =
                customer?.c_gigyaId ||
                basket?.c_gigyaGuestId ||
                localStorage.getItem('c_gigyaGuestId')

            if (!email && !id) return
            else
                return {
                    emailSHA256: email ? await hashSHA256(email) : null,
                    emailMD5: email ? md5(email) : null,
                    userId: id,
                    name: customerFirstName,
                    surname: customerLastName,
                    email,
                }
        },
        [
            customer?.firstName,
            customer?.lastName,
            customer?.email,
            customer?.customerId,
            basket?.customerInfo?.email,
            basket?.customerInfo?.customerId,
        ],
    )

    useEffect(() => {
        window.dataLayer = window.dataLayer || []
        const currentPath = `${location.pathname}${location?.search}`
        /* 
        const currentPath = `${location.pathname}${location?.search}`
        // Check if the realPageview event has already been pushed for the current location
        if (isRealPageviewPushed.current !== currentPath) {
            // Clear previous events in the dataLayer before pushing the new one
            window.dataLayer = window.dataLayer.filter((event) => event.event !== 'realPageview')
        */

        setTimeout(() => {
            ;(async () => {
                if (deviceType) {
                    const category = get_layer_page_category()
                    const pageSubCategory = get_layer_page_subcategory()

                    const virtualUrl = `${window?.location?.protocol}//${customParams?.currentHost}${location?.pathname}${location?.search}`

                    addLayerAtPositionZero(
                        virtual_page_view.bind(this, {
                            event: 'realPageview',
                            virtualUrl,
                            pageCountry: country,
                            pageLanguage: language,
                            device: deviceType,
                            ...(await hasCustomerData(customer, basket)),
                            pageCategory: category,
                            pageSubCategory: pageSubCategory,
                            statusCode: getStatusCode,
                            c_gigyaId:
                                customer?.c_gigyaId ||
                                basket?.c_gigyaGuestId ||
                                localStorage.getItem('c_gigyaGuestId'),
                        }),
                    )

                    isRealPageviewPushed.current = currentPath

                    if (locationChangeEvent.current !== virtualUrl) {
                        setTimeout(() => {
                            if (sendEvent) {
                                //console.log('[SFCC] Location change realPageview')
                                window.dispatchEvent(new Event('locationchange'))
                                locationChangeEvent.current = virtualUrl
                            }
                        }, 150)
                    }
                }
            })()
        }, 550)
    }, [
        isLoading,
        basketIsLoading,
        deviceType,
        getStatusCode,
        basket?.customerInfo?.email,
        basket?.customerInfo?.customerId,
        //location.pathname,
        //location.search,
        customer?.email,
        customer?.customerId,
        basket?.c_gigyaGuestId,
    ])

    useEffect(() => {
        window.dataLayer = window.dataLayer || []
        const currentPath = `${location.pathname}${location?.search}`
        //update the virtualUrl in Product is choosen from the Search modal
        const isFromISP = localStorage.getItem('fromISP')
        localStorage.removeItem('fromISP')
        let virtualUrl = `${window?.location?.protocol}//${customParams?.currentHost}${location?.pathname}${location?.search}`

        if (isRealPageviewPushed.current && isRealPageviewPushed.current !== currentPath) {
            if (!basketIsLoading || !isLoading || customer?.isGuest) {
                setTimeout(() => {
                    const category = get_layer_page_category()
                    const pageSubCategory = get_layer_page_subcategory()

                    if (category === 'detail' && isFromISP) {
                        const url = new URL(virtualUrl)
                        url.searchParams.set('fromISP', 'true')
                        virtualUrl = url.pathname + url.search
                    }

                    ;(async () => {
                        if (deviceType) {
                            addLayer(
                                virtual_page_view.bind(this, {
                                    virtualUrl,
                                    pageCountry: country,
                                    pageLanguage: language,
                                    device: deviceType,
                                    ...(await hasCustomerData(customer, basket)),
                                    pageCategory: category,
                                    pageSubCategory: pageSubCategory,
                                    statusCode: retrieve_status_code_gtm(),
                                    c_gigyaId:
                                        customer?.c_gigyaId ||
                                        basket?.c_gigyaGuestId ||
                                        localStorage.getItem('c_gigyaGuestId'),
                                }),
                            )

                            if (locationChangeEvent.current !== virtualUrl) {
                                setTimeout(() => {
                                    if (sendEvent) {
                                        window.dispatchEvent(new Event('locationchange'))
                                        locationChangeEvent.current = virtualUrl
                                    }
                                }, 150)
                            }
                        }
                    })()
                }, 500)
            }
        }
    }, [
        location.pathname,
        isLoading,
        basketIsLoading,
        deviceType,
        isRealPageviewPushed.current,
        basket?.customerInfo?.email,
        basket?.customerInfo?.customerId,
        customer?.email,
        customer?.customerId,
        basket?.c_gigyaGuestId,
    ])

    useEffect(() => {
        window.addEventListener('message', (event) => {
            if (event.data && event.data.action === 'setGTMCode') {
                // eslint-disable-next-line no-undef
                injectCode(event.data.code, event.data.position)
            }
            var dataLayerData = event.data
            if (dataLayerData && dataLayerData.event_source === 'custom_pixel_dataLayer_replace') {
                window.dataLayer = window.dataLayer || []
                window.dataLayer = dataLayerData.data
            }

            if (dataLayerData && dataLayerData.event_source === 'customPixel') {
                if (
                    dataLayerData.data[0].event === 'gtm.dom' ||
                    dataLayerData.data[0].event === 'gtm.load'
                ) {
                    return
                }
                window.dataLayer.push(dataLayerData.data[0])
            }
        })
    }, [])

    return <Box data-testid="gtm-page-view-component" />
}

export default GtmPageView
